import 'styles/custom.css';
import * as React from 'react';
import moment from 'moment';
import { PrimaryButton } from 'office-ui-fabric-react/lib/Button';
import { getRegionNames, getSyncRuns, triggerSyncRun, resetFailedRun } from '../components/Requests/Requests';
import {
    StandardScreen,
    StandardSearchBox,
    StandardDropdown,
    StandardButton,
    StatusMessage,
    FormRow,
} from '../components/Layout';
import Theme from '../styles/theme.js';
import { PageLayout } from '../components/Layout/BaseComponents.jsx';

const SYNC_TYPE_OPTIONS = [
    { key: 'Delta', text: 'Delta' },
    { key: 'Full', text: 'Full' },
    { key: 'Initial', text: 'Initial' },
];

const STATUS_COLORS = {
    Completed: '#107c10',
    Failed: '#a4262c',
    Pending: '#797775',
    PullingData: '#0078d4',
    SyncingPersons: '#0078d4',
    ResolvingPersonKeys: '#0078d4',
    SyncingRoles: '#0078d4',
    ResolvingRoleKeys: '#0078d4',
    SyncingUsers: '#0078d4',
    RemovingOrphans: '#0078d4',
};

function StatusBadge({ status }) {
    const color = STATUS_COLORS[status] || '#797775';
    return (
        <span style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '3px',
            backgroundColor: color,
            color: '#fff',
            fontSize: '12px',
            fontWeight: '600',
            fontFamily: Theme.typography.fontFamily,
            whiteSpace: 'nowrap',
        }}>
            {status}
        </span>
    );
}

function formatDuration(startedAt, completedAt) {
    if (!startedAt) return '—';
    const start = moment.utc(startedAt).toDate();
    const end = completedAt ? moment.utc(completedAt).toDate() : new Date();
    const ms = end - start;
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remaining = seconds % 60;
    return `${minutes}m ${remaining}s`;
}

function formatDateTime(dt) {
    if (!dt) return '—';
    return moment.utc(dt).local().toDate().toLocaleString();
}

class IDMSyncRuns extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            triggerLoading: false,
            resetLoadingId: null,
            error: '',
            message: '',
            spHostUrl: '',
            regionNameChosen: '',
            regionNames: [],
            syncType: 'Delta',
            runs: [],
            searchExecuted: false,
        };
        this._isMounted = false;
        this.pollingInterval = null;
    }

    componentDidMount() {
        this._isMounted = true;
        this.getRegionNames();
    }

    componentWillUnmount() {
        this._isMounted = false;
        this.stopPolling();
    }

    async getRegionNames() {
        try {
            const response = await getRegionNames(this.props.accessToken);
            if (!this._isMounted) return;
            if (response.status === 200) {
                const data = await response.json();
                this.setState({ regionNames: data.map(item => ({ key: item, text: item })) });
            } else {
                this.setState({ error: 'Failed to load environments.' });
            }
        } catch (e) {
            if (this._isMounted) this.setState({ error: 'Failed to load environments.' });
        }
    }

    async loadRuns(silent = false) {
        const { spHostUrl, regionNameChosen } = this.state;
        if (!spHostUrl || !regionNameChosen) return;

        if (!silent) this.setState({ loading: true, error: '', message: '' });

        try {
            const response = await getSyncRuns(this.props.accessToken, { spHostUrl, regionName: regionNameChosen });
            if (!this._isMounted) return;

            if (response.status === 200) {
                const runs = await response.json();
                this.setState({ runs, searchExecuted: true });
                this.managePolling(runs);
            } else {
                const text = await response.text();
                this.setState({ error: text || 'Failed to load sync runs.' });
                this.stopPolling();
            }
        } catch (e) {
            if (this._isMounted) {
                this.setState({ error: 'Failed to load sync runs.' });
                this.stopPolling();
            }
        } finally {
            if (this._isMounted) this.setState({ loading: false });
        }
    }

    managePolling(runs) {
        const hasActive = runs.some(r =>
            r.status !== 'Completed' && r.status !== 'Failed'
        );
        if (hasActive && !this.pollingInterval) {
            this.pollingInterval = setInterval(() => this.loadRuns(true), 10000);
        } else if (!hasActive) {
            this.stopPolling();
        }
    }

    stopPolling() {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
        }
    }

    async triggerRun() {
        const { spHostUrl, regionNameChosen, syncType } = this.state;
        this.setState({ triggerLoading: true, error: '', message: '' });

        try {
            const response = await triggerSyncRun(this.props.accessToken, {
                spHostUrl,
                regionName: regionNameChosen,
                syncType,
            });
            if (!this._isMounted) return;

            if (response.status === 200 || response.status === 202) {
                const data = await response.json();
                this.setState({
                    message: data.message || `Sync run created (ID: ${data.syncRunId}).`,
                });
                await this.loadRuns(true);
            } else if (response.status === 403) {
                this.setState({ error: 'You are not permitted to trigger sync runs. Admin access is required.' });
            } else if (response.status === 409) {
                this.setState({ error: 'An active sync run already exists for this tenant.' });
            } else {
                const text = await response.text();
                this.setState({ error: text || 'Failed to trigger sync run.' });
            }
        } catch (e) {
            if (this._isMounted) this.setState({ error: 'Failed to trigger sync run.' });
        } finally {
            if (this._isMounted) this.setState({ triggerLoading: false });
        }
    }

    async resetRun(syncRunId, resetType) {
        const { regionNameChosen } = this.state;
        this.setState({ resetLoadingId: syncRunId, error: '', message: '' });

        try {
            const response = await resetFailedRun(this.props.accessToken, {
                syncRunId,
                regionName: regionNameChosen,
                resetType,
            });
            if (!this._isMounted) return;

            if (response.status === 200) {
                const data = await response.json();
                this.setState({ message: data.message });
                await this.loadRuns(true);
            } else if (response.status === 403) {
                this.setState({ error: 'You are not permitted to reset sync runs. Admin access is required.' });
            } else {
                const text = await response.text();
                this.setState({ error: text || 'Failed to reset sync run.' });
            }
        } catch (e) {
            if (this._isMounted) this.setState({ error: 'Failed to reset sync run.' });
        } finally {
            if (this._isMounted) this.setState({ resetLoadingId: null });
        }
    }

    render() {
        const {
            loading, triggerLoading, resetLoadingId,
            error, message,
            spHostUrl, regionNameChosen, regionNames, syncType,
            runs, searchExecuted,
        } = this.state;

        const canSearch = spHostUrl && regionNameChosen;
        const isLoading = loading || triggerLoading;

        return (
            <PageLayout>
                <StandardScreen
                    title="IDM Sync Runs"
                    description="Trigger, monitor, and reset IDM Sync V2 runs for a tenant."
                    loading={isLoading}
                >
                    {/* Search & Trigger */}
                    <StandardSearchBox
                        label="SPHostUrl"
                        placeholder="https://tenant.sharepoint.com"
                        value={spHostUrl}
                        onChange={(val) => {
                            this.stopPolling();
                            this.setState({ spHostUrl: val, error: '', message: '', runs: [], searchExecuted: false });
                        }}
                    />

                    <StandardDropdown
                        label="Environment"
                        selectedKey={regionNameChosen}
                        onChange={(_, item) => {
                            this.stopPolling();
                            this.setState({ regionNameChosen: item.key, error: '', message: '', runs: [], searchExecuted: false });
                        }}
                        placeholder="Select Environment"
                        options={regionNames}
                        disabled={!regionNames.length}
                    />

                    <FormRow>
                        <StandardDropdown
                            label="Sync Type"
                            selectedKey={syncType}
                            onChange={(_, item) => this.setState({ syncType: item.key })}
                            options={SYNC_TYPE_OPTIONS}
                        />
                    </FormRow>

                    <div style={{ display: 'flex', gap: '12px', marginTop: Theme.spacing.sm }}>
                        <StandardButton
                            text="Search"
                            onClick={() => this.loadRuns()}
                            disabled={!canSearch}
                            size="wide"
                        />
                        <StandardButton
                            text="Trigger Run"
                            onClick={() => this.triggerRun()}
                            disabled={!canSearch || triggerLoading}
                            size="wide"
                        />
                    </div>

                    {error && <StatusMessage type="error">{error}</StatusMessage>}
                    {message && <StatusMessage type="success">{message}</StatusMessage>}

                    {/* Run History Table */}
                    {searchExecuted && (
                        <div style={{ marginTop: Theme.spacing.md, overflowX: 'auto' }}>
                            <table style={tableStyle}>
                                <thead>
                                    <tr>
                                        {['ID', 'Status', 'Type', 'Created', 'Started', 'Completed', 'Duration', 'Users', 'Groups', 'Retries', 'Error', 'Actions'].map(h => (
                                            <th key={h} style={thStyle}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {runs.length === 0 ? (
                                        <tr>
                                            <td colSpan={12} style={{ ...tdStyle, textAlign: 'center', color: Theme.colors.text }}>
                                                No sync runs found for this tenant.
                                            </td>
                                        </tr>
                                    ) : runs.map(run => (
                                        <tr key={run.id} style={{ borderBottom: '1px solid #edebe9' }}>
                                            <td style={tdStyle}>{run.id}</td>
                                            <td style={tdStyle}><StatusBadge status={run.status} /></td>
                                            <td style={tdStyle}>{run.syncType}</td>
                                            <td style={tdStyle}>{formatDateTime(run.createdAt)}</td>
                                            <td style={tdStyle}>{formatDateTime(run.startedAt)}</td>
                                            <td style={tdStyle}>{formatDateTime(run.completedAt)}</td>
                                            <td style={tdStyle}>{formatDuration(run.startedAt, run.completedAt)}</td>
                                            <td style={tdStyle}>{run.userCount.toLocaleString()}</td>
                                            <td style={tdStyle}>{run.groupCount.toLocaleString()}</td>
                                            <td style={tdStyle}>{run.watchdogRetryCount}</td>
                                            <td style={{ ...tdStyle, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                title={run.errorMessage || ''}>
                                                {run.errorMessage || '—'}
                                            </td>
                                            <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                                                {run.status === 'Failed' && (
                                                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                                        <PrimaryButton
                                                            text={resetLoadingId === run.id ? '…' : 'Reset to Start'}
                                                            onClick={() => this.resetRun(run.id, 'ResetToStart')}
                                                            disabled={resetLoadingId !== null}
                                                            styles={{ root: { height: 28, minWidth: 0, padding: '0 10px', fontSize: 12 } }}
                                                        />
                                                        {run.usersPulled && run.groupsPulled && (
                                                            <PrimaryButton
                                                                text={resetLoadingId === run.id ? '…' : 'Reset to Sync'}
                                                                onClick={() => this.resetRun(run.id, 'ResetToSync')}
                                                                disabled={resetLoadingId !== null}
                                                                styles={{ root: { height: 28, minWidth: 0, padding: '0 10px', fontSize: 12 } }}
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </StandardScreen>
            </PageLayout>
        );
    }
}

const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    fontFamily: Theme.typography.fontFamily,
    fontSize: Theme.typography.fontSize ? Theme.typography.fontSize.small : '13px',
};

const thStyle = {
    textAlign: 'left',
    padding: '8px 10px',
    borderBottom: '2px solid #edebe9',
    fontWeight: '600',
    color: Theme.colors ? Theme.colors.text : '#323130',
    whiteSpace: 'nowrap',
    backgroundColor: '#faf9f8',
};

const tdStyle = {
    padding: '8px 10px',
    color: Theme.colors ? Theme.colors.text : '#323130',
    verticalAlign: 'middle',
};

export default IDMSyncRuns;
